<script setup>
import { useI18n } from 'vue-i18n'
import UserName from '@/components/UserName.vue'
import TrackTitle from '@/components/TrackTitle.vue'
import { podiumClass, podiumColor } from '@/lib/podium'

/**
 * Standings with one column per track for a time event: the distance of every player from the best
 * time on each track, and the sum of the times, whose distance from the leader rides the tooltip.
 *
 * The fastest on a track has no gap to read: its cell is a stopwatch carrying the absolute time in
 * the tooltip, so a column of full times does not blow the table up on width.
 *
 * It only renders: loading, empty and error states stay with the caller.
 */
defineProps({
  /**
   * Rows, best first: `{ id, name, position?, total, gap, isMe, tracks: { [trackKey]: { position,
   * resultTime, gapTime } } }`. `total` and `gap` are already formatted (a time and its signed gap);
   * `position` is optional — the place in the list is used when it is missing — and a track without
   * an entry is shown as an empty cell.
   */
  rows: { type: Array, default: () => [] },
  /** Track keys, in the order the columns are shown. */
  trackKeys: { type: Array, default: () => [] },
})

const { t } = useI18n()

/** Place of a row: the one it carries, or the place it has in this list. */
const positionOf = (row, index) => row.position ?? index + 1
</script>

<template>
  <!-- Like the points matrix (see TrackPointsTable): a narrow screen scrolls the table sideways
       instead of squeezing every track. -->
  <div class="table-scroll">
    <v-table hover>
      <thead>
        <tr>
          <th class="text-center" style="width: 56px">{{ t('standings.position') }}</th>
          <th>{{ t('leaderboard.driver') }}</th>
          <th class="text-center text-no-wrap">{{ t('events.result') }}</th>
          <th v-for="key in trackKeys" :key="key" class="text-center text-no-wrap">
            <TrackTitle :track-id="key" variant="stacked" :flag="false" />
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(row, index) in rows"
          :key="row.id"
          :class="{ 'top-entry': positionOf(row, index) <= 3, 'me-row': row.isMe }"
        >
          <td class="text-center">
            <v-icon v-if="positionOf(row, index) <= 3" :class="podiumClass(positionOf(row, index))" size="20">
              mdi-medal
            </v-icon>
            <span v-else class="text-medium-emphasis">{{ positionOf(row, index) }}</span>
          </td>
          <td>
            <div class="d-flex align-center ga-2">
              <UserName :name="row.name" class="font-weight-medium" />
            </div>
          </td>
          <!-- The sum of the times alone: the real gap to the leader rides the tooltip, so a time
               and its gap do not widen the column. The leader has no gap to show. -->
          <td class="text-center text-no-wrap">
            <v-tooltip v-if="row.gap" :text="`(${row.gap})`">
              <template v-slot:activator="{ props: tip }">
                <span v-bind="tip" class="font-weight-bold text-mono">{{ row.total }}</span>
              </template>
            </v-tooltip>
            <span v-else class="font-weight-bold text-mono">{{ row.total }}</span>
          </td>
          <td v-for="key in trackKeys" :key="key" class="text-center text-no-wrap">
            <template v-if="row.tracks?.[key]">
              <!-- The fastest on the track: the time is what the column measures against, so it goes
                   in the tooltip and the cell stays narrow. -->
              <v-tooltip v-if="row.tracks[key].position === 1" :text="row.tracks[key].resultTime">
                <template v-slot:activator="{ props: tip }">
                  <v-icon v-bind="tip" icon="mdi-timer-outline" size="18" class="text-medium-emphasis" />
                </template>
              </v-tooltip>
              <span v-else class="text-medium-emphasis text-mono">{{ row.tracks[key].gapTime }}</span>
            </template>
            <span v-else class="text-medium-emphasis">–</span>
          </td>
        </tr>
      </tbody>
    </v-table>
  </div>
</template>

<style scoped>
.table-scroll {
  overflow-x: auto;
}

/* The first three places are tinted, the logged-in player more: the two add up on their own row. */
.top-entry td {
  background-color: rgba(var(--v-theme-primary), 0.05);
}

.me-row td {
  background-color: rgba(var(--v-theme-primary), 0.06);
}
</style>
